import { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Theme = "dark" | "light";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType>({ theme: "dark", setTheme: () => {} });

export const useTheme = () => useContext(ThemeContext);

// Theme is locked to dark (the light toggle was removed). The context shape is
// kept intact so any useTheme() consumer keeps compiling; setTheme is a no-op
// and any previously persisted "light" value is ignored. To re-enable light
// later, restore the stateful version and the MirrorHome toggle — the .light
// CSS in index.css is intentionally left in place for an easy revert.
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme] = useState<Theme>("dark");
  const setTheme = (_theme: Theme) => { /* locked to dark — no-op */ };

  useEffect(() => {
    // Force dark: pin storage and strip any stale .light class a previously
    // persisted preference may have applied to <html>.
    localStorage.setItem("vyv-theme", "dark");
    document.documentElement.classList.remove("light");
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
